import { forwardRef, type ButtonHTMLAttributes, type ComponentType } from 'react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> & {
  icon: ComponentType<IconProps>;
  /** Accessible name, required: an icon alone does not say what the button does. */
  label: string;
  /** Toggle state (aria-pressed), e.g. a saved bookmark. */
  pressed?: boolean;
  /** Pressed look: `saved` for a saved bookmark (today a green icon, sun fill in the next theme), `selected` for other toggles, `ink` for a negating toggle (exclude). */
  pressedTone?: 'saved' | 'selected' | 'ink';
  /** `outline` (default) sits on cards; `ghost` sits in toolbars. */
  variant?: 'outline' | 'ghost';
  size?: 'sm' | 'md';
};

const PRESSED = {
  saved: 'aria-pressed:bg-jc-saved aria-pressed:text-jc-on-saved aria-pressed:ring-jc-saved-ring',
  selected: 'aria-pressed:bg-jc-selected aria-pressed:text-jc-on-selected aria-pressed:ring-0',
  ink: 'aria-pressed:bg-jc-ink aria-pressed:text-jc-surface aria-pressed:ring-0',
};

/** A round icon-only button, 40px (the small size is the same, kept for the API). Binoculars for watching, the bell only for alerts. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    icon,
    label,
    pressed,
    pressedTone = 'saved',
    variant = 'outline',
    size = 'md',
    className,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      aria-label={label}
      title={rest.title ?? label}
      aria-pressed={pressed}
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-jc-control text-jc-ink-secondary',
        'transition duration-(--jc-duration-fast) ease-jc-out hover:bg-jc-elevated hover:text-jc-ink active:scale-97 motion-reduce:transition-none motion-reduce:active:scale-100 motion-reduce:active:opacity-70',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-2 focus-visible:ring-offset-jc-page',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'size-10',
        variant === 'outline' ? 'bg-jc-surface ring-1 ring-inset ring-jc-border-control' : 'bg-transparent',
        PRESSED[pressedTone],
        className,
      )}
    >
      <Icon icon={icon} size={size === 'md' ? 'md' : 'sm'} weight={pressed ? 'fill' : 'bold'} />
    </button>
  );
});
